import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ExchangeRate } from '../models/ExchangeRate';
import { exchangeRateService } from '../services/dataverse/exchangeRateService';

interface ExchangeRateContextValue {
  currentRate: ExchangeRate | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const ExchangeRateContext = createContext<ExchangeRateContextValue | null>(null);

export const ExchangeRateProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentRate, setCurrentRate] = useState<ExchangeRate | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const rate = await exchangeRateService.getCurrent();
      setCurrentRate(rate);
    } catch {
      // Endpoint is optional — the app doesn't rely on a persisted rate;
      // the assistant asks the user for the rate on every quote, so a 401
      // here should be silent, not a red console error on every reload.
      setCurrentRate(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo(() => ({ currentRate, loading, refresh }), [currentRate, loading]);
  return <ExchangeRateContext.Provider value={value}>{children}</ExchangeRateContext.Provider>;
};

export const useExchangeRateContext = (): ExchangeRateContextValue => {
  const context = useContext(ExchangeRateContext);
  if (!context) {
    throw new Error('useExchangeRateContext must be used within ExchangeRateProvider');
  }
  return context;
};
